// React
import React, { useCallback, useMemo, useState } from "react";

// Types
import { OfficeDropdownProps } from "./types";
import { DropdownOption } from "../Dropdown/types";
import { OfficeRow } from "services/api/offices/types";

// API
import { getOfficesAPI } from "services/api/offices/api";

// Pagination & Data
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";

// Hooks
import {
  DropdownCacheKey,
  buildDropdownCacheKey,
  useDropdownCache,
} from "services/hooks/useDropdownCache";

// UI Components
import { DropdownAutocomplete } from "../DropdownAutocomplete";

/**
 * Odabir poslovnice. Nema "kreiraj novu" — poslovnice se uređuju u Postavkama,
 * a ovdje se samo biraju.
 */
const OfficeDropdown: React.FC<OfficeDropdownProps> = (props: OfficeDropdownProps) => {
  const {
    name,
    disabled,
    required,
    label,
    error,
    value,
    direction,
    width,
    height,
    placeholder,
    tenantId,
    onChange,
  } = props;

  const [searchValue, setSearchValue] = useState<string>("");

  const pagination = initialDataWithPagination.pagination;

  // tenantId ulazi u ključ da se poslovnice ne prelijevaju između tenanta.
  const cacheKey = buildDropdownCacheKey(
    DropdownCacheKey.Offices,
    tenantId,
    searchValue
  );

  const { data, isLoading } = useDropdownCache<DataWithPagination<OfficeRow>>(
    cacheKey,
    () => getOfficesAPI(pagination, searchValue, tenantId),
    5 * 60 * 1000
  );

  const searchResults = data ?? initialDataWithPagination;

  const handleSearchFocus = useCallback(() => {
    setSearchValue("");
  }, []);

  const handleSearchChange = useCallback((val: string) => {
    setSearchValue(val);
  }, []);

  const officeItems: DropdownOption[] = useMemo(
    () =>
      searchResults.data.map((office) => ({
        value: office.id,
        text: office.name,
      })),
    [searchResults.data]
  );

  return (
    <DropdownAutocomplete
      label={label}
      name={name}
      disabled={disabled}
      required={required}
      placeholder={placeholder}
      value={value}
      direction={direction}
      width={width}
      height={height}
      options={officeItems}
      onChange={onChange}
      onSearchChange={handleSearchChange}
      onSearchFocus={handleSearchFocus}
      error={error}
      isLoading={isLoading}
    />
  );
};

export default React.memo(OfficeDropdown);
